import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// axios instance that always sends the httpOnly cookie (auth)
export const http = axios.create({ baseURL: API, withCredentials: true });
try { http.defaults.headers.common["X-Source"] = localStorage.getItem("rsell_src") || "direct"; } catch {}

export function getAnonSessionId() {
  let sid = localStorage.getItem("resell_ai_sid");
  if (!sid) {
    sid = "sid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("resell_ai_sid", sid);
  }
  return sid;
}

// ---- Traffic source attribution (first-touch, persisted) ----
// Captured from ?src=... (preferred), utm_source, or document.referrer host.
try {
  if (!localStorage.getItem("rsell_src")) {
    const q = new URLSearchParams(window.location.search);
    let s = q.get("src") || q.get("utm_source") || "";
    if (!s && document.referrer) {
      try { s = "ref:" + new URL(document.referrer).hostname.replace("www.", ""); } catch {}
    }
    localStorage.setItem("rsell_src", (s || "direct").slice(0, 40));
  }
} catch {}

// Backwards-compat: components still call getSessionId() — now returns
// the logged-in effective session (u:userId) if present, else anon.
export function getSessionId() {
  const overr = localStorage.getItem("resell_ai_effective_sid");
  return overr || getAnonSessionId();
}

export function setEffectiveSessionId(sid) {
  if (sid) localStorage.setItem("resell_ai_effective_sid", sid);
}

export function clearEffectiveSessionId() {
  localStorage.removeItem("resell_ai_effective_sid");
}

export const apiGetCredits = (sid) => http.get(`/credits/${sid}`).then(r => r.data);
export const apiPurchase = (sid, plan) => http.post(`/credits/purchase`, { session_id: sid, plan }).then(r => r.data);
export const apiDemoScan = () => http.post(`/scan/demo`).then(r => r.data);
export const apiScan = (sid, brand_lock, images, category = "apparel", captcha_token = "", device_fp = null) => http.post(`/scan`, { session_id: sid, brand_lock, images, category, captcha_token, device_fp }).then(r => r.data);
export const apiVault = (sid) => http.get(`/vault/${sid}`).then(r => r.data);
export const csvUrl = (scanId) => `${API}/export/csv/${scanId}`;

// Auth
export const apiAuthMe = () => http.get(`/auth/me`).then(r => r.data);
export const apiAuthExchange = (sessionId, anonSessionId) =>
  http.post(`/auth/session`, { anon_session_id: anonSessionId }, { headers: { "X-Session-ID": sessionId } }).then(r => r.data);
export const apiAuthLogout = () => http.post(`/auth/logout`).then(r => r.data);

// Payments (Whop)
export const apiWhopCheckout = (tier, sessionId, checkoutEmail = null, affiliateCode = null) =>
  http.post(`/whop/checkout`, {
    tier,
    session_id: sessionId,
    origin_url: window.location.origin,
    ...(checkoutEmail ? { checkout_email: checkoutEmail } : {}),
    ...(affiliateCode ? { affiliate_code: affiliateCode } : {}),
  }).then(r => r.data);

// Affiliate program — public validation + admin CRUD (Whop-native)
export const apiValidateAffiliate = (code) =>
  http.get(`/affiliates/validate`, { params: { code } }).then(r => r.data);
export const apiAdminListAffiliates = () =>
  http.get(`/admin/affiliates`).then(r => r.data);
export const apiAdminCreateAffiliate = (payload) =>
  http.post(`/admin/affiliates`, payload).then(r => r.data);
export const apiAdminArchiveAffiliate = (code) =>
  http.delete(`/admin/affiliates/${encodeURIComponent(code)}`).then(r => r.data);
export const apiAdminAnalytics = () =>
  http.get(`/admin/analytics`).then(r => r.data);
export const apiAdminAffiliateDetail = (code) =>
  http.get(`/admin/affiliates/${encodeURIComponent(code)}/detail`).then(r => r.data);
export const apiWhopStatus = (whopRef) =>
  http.get(`/whop/status/${whopRef}`).then(r => r.data);

// Pull-based sync (replaces webhooks)
export const apiSyncCredits = (sessionId) =>
  http.post(`/user/sync-credits`, { session_id: sessionId }).then(r => r.data);

// Live eBay sold comps (item-specific, tier-gated server-side)
export const apiEbayComps = (sessionId, report) =>
  http.post(`/comps/ebay`, { session_id: sessionId, report }).then(r => r.data);

// -- Async job polling helper --------------------------------------------
// Both /scan/enrich and /photo-studio/enhance return a job_id instantly and
// let us poll a status endpoint. This dodges Cloudflare's 100-second edge
// timeout on slow generations. Poll every 2.5s up to 3 minutes, then fail
// so users never sit on a spinner forever.
const _pollJob = async (statusPath, jobId, { onProgress } = {}) => {
  const MAX_ATTEMPTS = 72;   // 72 * 2.5s = 180s
  const INTERVAL_MS = 2500;
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    const { data } = await http.get(`${statusPath}/${jobId}`);
    if (typeof onProgress === "function") onProgress(data);
    if (data.status === "done") return data.result;
    if (data.status === "failed") {
      const e = new Error(data.error || "generation_failed");
      e.jobError = data.error;
      throw e;
    }
    await new Promise((r) => setTimeout(r, INTERVAL_MS));
  }
  throw new Error("generation_timeout");
};

// On-demand enrichment (Claude Haiku 4.5) — listings + negotiation lines
export const apiEnrichScan = async (scanId, kind, { onProgress } = {}) => {
  const { data } = await http.post(`/scan/enrich`, { scan_id: scanId, kind });
  if (data.status === "done" && data.cached) {
    // Server may have returned a done-status job (cache hit) — poll once
    // to grab the payload without any real wait.
    return _pollJob(`/scan/enrich/status`, data.job_id, { onProgress });
  }
  return _pollJob(`/scan/enrich/status`, data.job_id, { onProgress });
};

// Photo Studio (Reseller/Pro only) — segment + composite
export const apiPhotoStudio = async (scanId, imageIndex = 0, backdrop = "warm-oak", { onProgress } = {}) => {
  const { data } = await http.post(`/photo-studio/enhance`, {
    scan_id: scanId, image_index: imageIndex, backdrop,
  });
  return _pollJob(`/photo-studio/status`, data.job_id, { onProgress });
};

// Studio Pro (generative, 1 credit; auto-refund on failure/fidelity reject)
export const apiPhotoStudioPro = async (scanId, sessionId, imageB64, imageIndex = 0, { onProgress } = {}) => {
  const { data } = await http.post(`/photo-studio/pro`, {
    scan_id: scanId, session_id: sessionId, image_b64: imageB64, index: imageIndex,
  });
  return _pollJob(`/photo-studio/status`, data.job_id, { onProgress });
};

// Locked preview (free/thrifter) — cached per scan, shown blurred
export const apiPhotoStudioPreview = (scanId) =>
  http.post(`/photo-studio/preview`, { scan_id: scanId }).then(r => r.data);

export const apiSessionSummary = (sessionId) =>
  http.get(`/session/summary/${encodeURIComponent(sessionId)}`).then(r => r.data);
export const apiVaultExportUrl = (sessionId) =>
  `${API}/vault/export.csv?session_id=${encodeURIComponent(sessionId)}`;

// Legal / GDPR
export const apiRecordConsent = (sessionId, versions) =>
  http.post(`/policies/consent`, { session_id: sessionId, versions }).then(r => r.data);
export const apiPolicyVersions = () => http.get(`/policies/versions`).then(r => r.data);
export const apiExportMyData = () => http.get(`/user/data-export`).then(r => r.data);
export const apiDeleteMyAccount = () => http.delete(`/user/account`).then(r => r.data);

// Fetch a URL image and return base64 (no data URL prefix)
export async function urlToBase64(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const s = reader.result;
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const s = reader.result;
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Client-side JPEG compression via canvas. Longest edge → maxEdge px, quality 0.8.
// Returns { base64 (no prefix), preview (data URL), originalSize, compressedSize }.
// Optional `grayscale=true` — used for tag photos, cuts vision tokens with no
// legibility loss because OCR ignores colour.
export async function compressImageToBase64(file, maxEdge = 1600, quality = 0.8, grayscale = false) {
  if (!file || !file.type.startsWith("image/")) {
    throw new Error("Not an image file");
  }
  const bitmap = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
  const { width: w0, height: h0 } = bitmap;
  const scale = Math.min(1, maxEdge / Math.max(w0, h0));
  const w = Math.round(w0 * scale);
  const h = Math.round(h0 * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (grayscale) {
    ctx.filter = "grayscale(1)";
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  URL.revokeObjectURL(bitmap.src);
  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  const idx = dataUrl.indexOf(",");
  const base64 = idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
  const compressedSize = Math.round((base64.length * 3) / 4);
  return { base64, preview: dataUrl, originalSize: file.size, compressedSize };
}

// §2.3 Pre-flight image quality check. Runs in the browser before any API call.
// Rejects blurry, too-dark, or too-small photos so a bad shot doesn't burn the
// user's only free scan. Returns { ok:bool, reason?:string, luminance, sharpness }.
export async function checkImageQuality(file) {
  const bitmap = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
  const { width: w0, height: h0 } = bitmap;
  const longEdge = Math.max(w0, h0);
  if (longEdge < 400) {
    URL.revokeObjectURL(bitmap.src);
    return { ok: false, reason: "photo is too small (min 400px)", luminance: 0, sharpness: 0 };
  }
  // Downscale to 200px longest edge for fast measurement.
  const scale = 200 / longEdge;
  const w = Math.round(w0 * scale);
  const h = Math.round(h0 * scale);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const cx = c.getContext("2d");
  cx.drawImage(bitmap, 0, 0, w, h);
  URL.revokeObjectURL(bitmap.src);
  const { data } = cx.getImageData(0, 0, w, h);
  // Compute per-pixel luminance array (Rec. 709), then Laplacian variance.
  const lum = new Float32Array(w * h);
  let sum = 0;
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const l = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    lum[j] = l; sum += l;
  }
  const meanLum = sum / lum.length;
  // Laplacian kernel: 4-neighbour discrete Laplacian
  let s = 0, s2 = 0, n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap = 4 * lum[i] - lum[i - 1] - lum[i + 1] - lum[i - w] - lum[i + w];
      s += lap; s2 += lap * lap; n++;
    }
  }
  const meanLap = s / n;
  const variance = s2 / n - meanLap * meanLap;
  const sharpness = variance;
  if (meanLum < 30) return { ok: false, reason: "photo is too dark", luminance: meanLum, sharpness };
  if (sharpness < 60) return { ok: false, reason: "photo looks blurry", luminance: meanLum, sharpness };
  return { ok: true, luminance: meanLum, sharpness };
}
